import React, { useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Stack, Box, CircularProgress, Divider, Tooltip } from '@mui/material';
import { 
  AccountBalanceWallet, 
  AccessTime, 
  Language, 
  Hotel, 
  Restaurant, 
  SportsTennis, 
  AttachMoney,
  TrendingUp,
  TrendingDown,
  Receipt,
  Payment
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const WEBSITE_BASE_URL = process.env.REACT_APP_WEBSITE_BASE_URL;

const StatisticsCards = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState({
    summary: {
      total_payments: 0,
      total_revenue: 0,
      total_expenses: 0,
      total_expenses_amount: 0,
      net_revenue: 0
    },
    breakdown: {
      bookings: { count: 0, amount: 0 },
      food_beverage: { count: 0, amount: 0 },
      facilities: { count: 0, amount: 0 },
      other_incomes: { count: 0, amount: 0 },
      expenses: { count: 0, amount: 0 }
    },
    recent_transactions: []
  });
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const hotelCode = process.env.REACT_APP_HOTEL_CODE;

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(
        `${API_BASE_URL}/hotel/admin/total_payment_Today.php?hotel_code=${hotelCode}`
      );

      if (response.data.success) {
        setPaymentData(response.data.data);
      } else {
        throw new Error(response.data.error || 'Failed to fetch payment data');
      }
    } catch (err) {
      console.error('Error fetching payment data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchPaymentData();
    const refreshInterval = setInterval(fetchPaymentData, 300000);
    return () => clearInterval(refreshInterval);
  }, [hotelCode]);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: true,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₦0.00';
    return `₦${parseFloat(amount).toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  const formatNumber = (number) => {
    if (number === null || number === undefined) return '0';
    return number.toLocaleString();
  };

  const handleCardClick = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const cards = [
    {
      title: 'Time',
      value: formatTime(currentTime),
      icon: <AccessTime fontSize="medium" />,
      gradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
      glossy: true,
      shadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
      url: null
    },
    
    {
      title: 'Total Revenue',
      value: formatCurrency(paymentData.summary.total_revenue),
      icon: <AttachMoney fontSize="medium" />,
      gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
      glossy: true,
      shadow: '0 4px 20px rgba(46, 125, 50, 0.3)',
      url: null,
      subtitle: `${formatNumber(paymentData.summary.total_payments)} transactions`
    },
    {
      title: 'Net Revenue',
      value: formatCurrency(paymentData.summary.net_revenue),
      icon: <TrendingUp fontSize="medium" />,
      gradient: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
      glossy: true,
      shadow: '0 4px 20px rgba(67, 160, 71, 0.3)',
      url: null,
      subtitle: `After expenses: ${formatCurrency(paymentData.summary.total_expenses_amount)}`
    },
  ];

  const breakdownCards = [
    {
      title: 'Hotel Bookings',
      value: formatCurrency(paymentData.breakdown.bookings.amount),
      icon: <Hotel fontSize="medium" />,
      count: paymentData.breakdown.bookings.count,
      gradient: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
      glossy: true
    },
    {
      title: 'Food & Beverage',
      value: formatCurrency(paymentData.breakdown.food_beverage.amount),
      icon: <Restaurant fontSize="medium" />,
      count: paymentData.breakdown.food_beverage.count,
      gradient: 'linear-gradient(135deg, #c2185b 0%, #880e4f 100%)',
      glossy: true
    },
    {
      title: 'Facility Bookings',
      value: formatCurrency(paymentData.breakdown.facilities.amount),
      icon: <SportsTennis fontSize="medium" />,
      count: paymentData.breakdown.facilities.count,
      gradient: 'linear-gradient(135deg, #ff8f00 0%, #e65100 100%)',
      glossy: true
    },
    {
      title: 'Other Incomes',
      value: formatCurrency(paymentData.breakdown.other_incomes.amount),
      icon: <Receipt fontSize="medium" />,
      count: paymentData.breakdown.other_incomes.count,
      gradient: 'linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)',
      glossy: true
    },
    {
      title: 'Expenses',
      value: formatCurrency(paymentData.breakdown.expenses.amount),
      icon: <TrendingDown fontSize="medium" />,
      count: paymentData.breakdown.expenses.count,
      gradient: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
      glossy: true
    }
  ];

  if (loading) {
    return (
      <Grid container spacing={3}>
        {[...cards, ...breakdownCards].map((card, idx) => (
          <Grid item xs={12} sm={6} lg={4} xl={2.4} key={idx}>
            <Card
              sx={{
                height: 90,
                cursor: 'default',
                background: card.gradient,
                color: card.textColor || 'white',
                borderRadius: 3,
                boxShadow: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={24} sx={{ color: card.textColor || 'white' }} />
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Box>
      {error && (
        <Box sx={{ mb: 2, p: 1, backgroundColor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          <Typography variant="body2">Error: {error}</Typography>
        </Box>
      )}
      
      {/* Main Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {cards.map((card, idx) => (
          <Grid item xs={12} sm={6} lg={3} key={idx}>
            <Card
              onClick={() => handleCardClick(card.url)}
              sx={{
                height: card.subtitle ? 100 : 90,
                cursor: card.url ? 'pointer' : 'default',
                background: card.gradient,
                color: card.textColor || 'white',
                borderRadius: 3,
                boxShadow: card.shadow,
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&::before': card.glossy ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '30%',
                  background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 100%)',
                  borderRadius: '8px 8px 0 0',
                } : {},
                '&:hover': {
                  transform: card.url ? 'scale(1.02)' : 'none',
                  boxShadow: card.url ? 8 : card.shadow,
                },
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Box flex={1}>
                    <Typography variant="subtitle2" sx={{ opacity: 0.9, fontSize: '0.875rem', mb: 0.5 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.5rem' }}>
                      {card.value}
                    </Typography>
                    {card.subtitle && (
                      <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                        {card.subtitle}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: card.textColor ? 'rgba(25, 118, 210, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      flexShrink: 0,
                    }}
                  >
                    {card.icon}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Revenue Breakdown Cards */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Today's Revenue Breakdown by Channel
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {breakdownCards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={idx}>
            <Tooltip title={`${card.count} transaction(s)`} arrow>
              <Card
                sx={{
                  height: 100,
                  background: card.gradient,
                  color: 'white',
                  borderRadius: 3,
                  boxShadow: card.shadow,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': card.glossy ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '30%',
                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 100%)',
                  } : {},
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Box flex={1}>
                      <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                        {card.title}
                      </Typography>
                      <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                        {card.value}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.65rem' }}>
                        {card.count} transaction(s)
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      {card.icon}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      {/* Recent Transactions */}
      {paymentData.recent_transactions && paymentData.recent_transactions.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Recent Transactions
          </Typography>
          <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {paymentData.recent_transactions.map((transaction, idx) => (
                <Box key={idx}>
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {transaction.source}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ref: {transaction.reference}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {transaction.customer_name}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        {formatCurrency(transaction.amount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {transaction.payment_method}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {new Date(transaction.transaction_date).toLocaleTimeString()}
                      </Typography>
                    </Box>
                  </Box>
                  {idx < paymentData.recent_transactions.length - 1 && <Divider />}
                </Box>
              ))}
            </Box>
          </Card>
        </>
      )}
    </Box>
  );
};

export default StatisticsCards;